import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLang, t, tx, getLocale, i18nField } from '../context/LanguageContext';
import translations from '../i18n/translations';
import { ArrowRight, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import { SEO_TEXTES } from '../data/seoTextes';
import MarkdownView from '../components/MarkdownView';
import newsSnapshot from '../data/news-snapshot.json';

const PAGE_SIZE = 24;


const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

export default function News() {
  const { lang } = useLang();
  const n = translations.news;
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const [activeCategory, setActiveCategory] = useState(null);

  const filterByCategory = (list, cat) => (cat ? list.filter((a) => a.category === cat) : list);
  const sliceForPage = (list, page) => list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const [data, setData] = useState(() => {
    const filtered = filterByCategory(newsSnapshot, null);
    return {
      articles: sliceForPage(filtered, currentPage),
      total: filtered.length,
      page: currentPage,
      total_pages: Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)),
    };
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', String(currentPage));
    params.set('page_size', String(PAGE_SIZE));
    if (activeCategory) params.set('category', activeCategory);
    fetch(`/api/news?${params}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((payload) => {
        if (cancelled) return;
        if (payload && Array.isArray(payload.articles)) {
          setData({
            articles: payload.articles,
            total: payload.total || 0,
            page: payload.page || currentPage,
            total_pages: payload.total_pages || 1,
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        const filtered = filterByCategory(newsSnapshot, activeCategory);
        setData({
          articles: sliceForPage(filtered, currentPage),
          total: filtered.length,
          page: currentPage,
          total_pages: Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)),
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeCategory, currentPage]);

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setSearchParams({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPage = (n) => {
    if (n < 1 || n > data.total_pages || n === currentPage) return;
    setSearchParams(n === 1 ? {} : { page: String(n) });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const categories = Object.entries(n.categories);
  const articles = data.articles;

  return (
    <>
      <SEO
        title={currentPage > 1
          ? `Actualités METERIS — Page ${currentPage} sur ${data.total_pages}`
          : SEO_TEXTES['/actualites'].title}
        description={SEO_TEXTES['/actualites'].description}
        path={currentPage > 1 ? `/actualites?page=${currentPage}` : '/actualites'}
      />
    <main data-testid="news-page">
      {/* Hero */}
      <section className="pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-4">MÉTÉRIS</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-6">{t(n.title, lang)}</h1>
            <p className="text-xl text-slate-300 max-w-2xl">{t(n.subtitle, lang)}</p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2" data-testid="category-filter">
            <button
              onClick={() => handleCategoryChange(null)}
              data-testid="category-all"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                !activeCategory ? 'bg-[#0099D6] text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10'
              }`}
            >
              {t(n.all, lang)}
            </button>
            {categories.map(([key, label]) => (
              <button
                key={key}
                onClick={() => handleCategoryChange(key)}
                data-testid={`category-${key}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeCategory === key ? 'bg-[#0099D6] text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10'
                }`}
              >
                {t(label, lang)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="pb-24 md:pb-32" data-testid="articles-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-slate-400">{t(translations.common.loading, lang)}</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-400">{tx(lang, 'Aucun article pour le moment.', 'No articles yet.', { es: 'Sin artículos por ahora.', de: 'Noch keine Artikel.', it: 'Nessun articolo per ora.', pt: 'Ainda sem artigos.' })}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article, i) => (
                <motion.div
                  key={article.id || i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                >
                  <Link
                    to={`/actualites/${article.slug}`}
                    data-testid={`article-card-${article.slug}`}
                    className="block bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl overflow-hidden hover:border-[#0099D6]/30 transition-all duration-300 group h-full"
                  >
                    {article.image_url && (
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={article.image_url}
                          alt={i18nField(article, 'title', lang)}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#15171E] to-transparent" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        {article.category && n.categories[article.category] && (
                          <span className="px-2.5 py-1 rounded-full bg-[#0099D6]/10 text-xs font-medium text-[#0099D6]">
                            {t(n.categories[article.category], lang)}
                          </span>
                        )}
                        {(article.publish_date || article.created_at) && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Calendar className="w-3 h-3" />
                            {new Date(article.publish_date || article.created_at).toLocaleDateString(getLocale(lang))}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#0099D6] transition-colors line-clamp-2">
                        {i18nField(article, 'title', lang)}
                      </h3>
                      <p className="text-sm text-slate-400 leading-relaxed line-clamp-3">
                        <MarkdownView markdown={i18nField(article, 'excerpt', lang)} inline />
                      </p>
                      <div className="mt-4 flex items-center gap-2 text-sm font-medium text-[#0099D6] opacity-0 group-hover:opacity-100 transition-opacity">
                        {t(n.read_more, lang)}
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          {data.total_pages > 1 && (
            <nav className="flex items-center justify-center gap-2 mt-12 flex-wrap" aria-label="Pagination" data-testid="pagination">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label={tx(lang, 'Page précédente', 'Previous page', { es: 'Página anterior', de: 'Vorherige Seite', it: 'Pagina precedente', pt: 'Página anterior' })}
                className="p-2 rounded-lg bg-white/5 text-slate-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: data.total_pages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => goToPage(p)}
                  aria-current={p === currentPage ? 'page' : undefined}
                  className={`min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    p === currentPage
                      ? 'bg-[#0099D6] text-white'
                      : 'bg-white/5 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === data.total_pages}
                aria-label={tx(lang, 'Page suivante', 'Next page', { es: 'Página siguiente', de: 'Nächste Seite', it: 'Pagina successiva', pt: 'Próxima página' })}
                className="p-2 rounded-lg bg-white/5 text-slate-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </nav>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-transparent to-[#0099D6]/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">{t(n.cta_title, lang)}</h2>
            <p className="text-lg text-slate-300 mb-8">{t(n.cta_text, lang)}</p>
            <Link
              to="/prendre-rdv"
              data-testid="news-cta"
              className="inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-4 px-8 rounded-lg shadow-[0_0_20px_rgba(0,153,214,0.3)] transition-all hover:scale-105"
            >
              {t(translations.common.rdv_cta, lang)}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
    </>
  );
}
